import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";

export default function About() {
  return (
    <div>
      <Seo
        path="/about"
        title="About Steady Path Funding"
        description="An independent commercial finance brokerage. We work for the borrower — placing SBA, bank, and fintech capital across 75+ lender partners."
      />
      <section className="pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-7">
            <div className="overline">About · The brokerage</div>
            <h1 className="mt-6 font-serif text-6xl md:text-[8vw] leading-[0.92] tracking-tight">
              A steady hand for the <em className="italic text-[color:var(--gold)]">unsteady work</em> of building.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-4">
            <p className="text-[color:var(--bone)]/80 leading-relaxed text-lg">
              Steady Path Funding is an independent commercial finance
              brokerage. We work exclusively for the borrower. That means we
              don't answer to a single lender's underwriting box — we shop
              the market on your behalf, and we get paid only when your
              funding closes.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid grid-cols-12 gap-8 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-5"
          >
            <div className="relative aspect-[4/5] p-10 md:p-12 flex flex-col justify-between bg-[color:var(--surface)] border border-black/10">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                Manifesto · Steady Path
              </div>
              <blockquote className="font-serif italic text-3xl md:text-4xl leading-[1.15] text-[color:var(--bone)]">
                “Steady capital.
                <br />
                <span className="text-[color:var(--gold)]">Uncommon paths.</span>
                <br />
                For the businesses that build the country.”
              </blockquote>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--bone)]/50">
                Est. Chicagoland · Serving all 50 states
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7 space-y-14">
            {[
              {
                num: "01",
                title: "Independent",
                body:
                  "We're not a lender. We don't have a single balance sheet to protect. Our job is to see the whole market and place you where you'll be treated best — not where it's easiest for us.",
              },
              {
                num: "02",
                title: "Broker, not bureau",
                body:
                  "One application, 75+ lenders shopped. We negotiate rates, fees, and covenants on your behalf and translate the fine print into plain English before you sign anything.",
              },
              {
                num: "03",
                title: "No cost to apply",
                body:
                  "Our brokerage fee is paid by the lender at close — never by you, never up front. Some fintech lenders themselves charge origination fees that get netted from your funded amount; when that's the case, you'll see the exact dollar figure on your offer sheet before you sign anything.",
              },
            ].map((v) => (
              <div key={v.num} className="grid grid-cols-12 gap-4 border-t border-black/10 pt-8">
                <div className="col-span-2 font-mono text-[color:var(--gold)] text-sm tracking-widest">
                  {v.num}
                </div>
                <div className="col-span-10">
                  <h3 className="font-serif text-3xl md:text-4xl leading-tight">{v.title}</h3>
                  <p className="mt-3 text-[color:var(--bone)]/75 leading-relaxed max-w-lg">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-black/10 py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          {[
            ["$500M+", "Placed"],
            ["75+", "Lender partners"],
            ["24 hr", "Fastest funding"],
            ["1 hr", "Broker callback"],
          ].map(([n, l]) => (
            <div key={l} className="border-t border-black/10 pt-6">
              <div className="font-serif text-5xl md:text-6xl text-[color:var(--bone)]">{n}</div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-black/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between gap-8 items-start md:items-end">
          <h3 className="font-serif text-4xl md:text-5xl leading-tight max-w-xl">
            Prefer to talk first? We do that too.
          </h3>
          <Link to="/contact" className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
            Contact a broker →
          </Link>
        </div>
      </section>
    </div>
  );
}
